import { useMutation, useQueryClient, type MutationMeta } from '@tanstack/react-query';
import type { IIssue } from '../../../trusstor/index.js';
import { useClientApis } from '../../hooks/ClientApisProvider.js';
import { beginIssuePatch, patchCachedIssue, rollbackIssue } from './issueCache.js';

/** `meta`: the mutation's react-query meta — `OWNS_ERROR_FEEDBACK` when the caller reports a failure itself. */
export function useUpdateIssue(siteId: string | null, meta?: MutationMeta) {
  const { updateIssueApi } = useClientApis().issues;
  const qc = useQueryClient();
  return useMutation({
    meta,
    mutationFn: ({ issueId, patch }: { issueId: string; patch: Partial<IIssue> }) => {
      if (!siteId) throw new Error('updateIssue called without siteId');
      return updateIssueApi(siteId, issueId, patch);
    },
    // Optimistic: merge the patch into the cached issue at once, so toggles
    // (critical/safety) flip instantly instead of waiting for the PUT.
    onMutate: async ({ issueId, patch }) => ({
      previous: await beginIssuePatch(qc, siteId, issueId, (i) => ({ ...i, ...patch })),
    }),
    onError: (_err, { issueId }, ctx) => rollbackIssue(qc, siteId, ctx?.previous, issueId),
    // Reconcile with the server's copy for ONLY the keys this mutation set, so
    // a concurrent quick-status move's optimistic state isn't clobbered by a
    // stale full object when two mutations race.
    onSuccess: (updated, { issueId, patch }) =>
      patchCachedIssue(qc, siteId, issueId, (i) => ({
        ...i,
        ...Object.fromEntries(Object.keys(patch).map((k) => [k, updated[k as keyof IIssue]])),
      })),
  });
}
